// Shared by browser-side-bloc-controller.ts (WorkerBloc channel errors) and
// websocket-subject-multiplex-shared.ts (CpWs/BpWs channel errors) - both are the single
// choke point their respective error sources cross, so tagging origin here once covers both
// instead of duplicating (and re-diverging) the same enrichment in each file.
// See BCF-V2-PREVIEW-4P / BCF-V2-PREVIEW-56.

function isHttpErrorShaped(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.prototype.hasOwnProperty.call(value, 'url') &&
    Object.prototype.hasOwnProperty.call(value, 'status')
  );
}

function describeNonErrorValue(value: unknown): string {
  if (typeof CloseEvent !== 'undefined' && value instanceof CloseEvent) {
    return `CloseEvent(code=${value.code}, reason=${value.reason || 'n/a'}, wasClean=${value.wasClean})`;
  }
  if (typeof Event !== 'undefined' && value instanceof Event) {
    return `Event(type=${value.type})`;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Tags an error crossing a shared transport boundary with where it came from, without losing
 * information the rest of the Sentry pipeline depends on:
 * - a real Error is mutated in place (message prefixed) - never re-wrapped, so its `.cause`
 *   is never set to itself.
 * - an HttpError-shaped plain object (duck-typed the same way WorkerSideMessengerInternal
 *   does) is copied field-by-field, so a real `HttpError` keeps `_type`/`status`/`url` and
 *   `isHttpError()` downstream still recognizes it - only `statusText` gets the origin prefix.
 *   Foreign http-shaped objects reaching here may carry no `statusText` at all (the legacy
 *   `{ url, status, message }` flattening in WorkerSideMessengerInternal produces exactly that),
 *   hence the fallback - without it the prefix read literally "[origin] undefined".
 * - anything else (a raw WebSocket Event/CloseEvent, a string, ...) is wrapped in a new Error
 *   with a human-readable description and the original value kept as `.cause`.
 */
export function enrichOriginError(origin: string, error: unknown): unknown {
  if (error instanceof Error) {
    error.message = `[${origin}] ${error.message}`;
    return error;
  }

  if (isHttpErrorShaped(error)) {
    const detail: unknown = error['statusText'] ?? error['message'] ?? error['status'];
    return { ...error, statusText: `[${origin}] ${String(detail)}` };
  }

  const wrapped: Error = new Error(`[${origin}] ${describeNonErrorValue(error)}`);
  (wrapped as Error & { cause?: unknown }).cause = error;
  return wrapped;
}
